import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel as MuiInputLabel,
  OutlinedInput as MuiOutlinedInput,
  OutlinedInputProps,
} from "@mui/material";
import React, { useRef, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import styled from "styled-components";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

interface StyledOutlinedInputProps extends OutlinedInputProps {
  has_error?: string | undefined; // Updated to optional for better type compatibility
}

// Styled OutlinedInput
const StyledOutlinedInput = styled(MuiOutlinedInput)<StyledOutlinedInputProps>(
  ({ theme }) => ({
    "& .MuiOutlinedInput-input": {
      width: "85px",
      padding: "0",
      paddingTop: "18px",
      fontFamily: "var(--font-Medium)",
      fontSize: "12px",
      caretColor: theme.colors_ds.PRIMARY_ICON_MAIN,
      // textAlign: "right",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    legend: {
      display: "none",
    },
    fieldset: {
      top: 0,
    },
  })
);

const StyledInputLabel = styled(MuiInputLabel)(({ theme }) => ({
  fontFamily: "var(--font-Medium)",
  fontSize: "10px",
  backgroundColor: "transparent",
  transition: "transform 0.2s ease-in-out",
  color: theme.colors_ds.NEUTRAL_TEXT_WEAK,

  "&.Mui-focused": {
    color: theme.colors_ds.PRIMARY_ICON_MAIN,
  },
}));

const StyledFormHelperText = styled(FormHelperText)`
  width: 500px;
  font-family: var(--font-Medium), sans-serif;
  font-size: 10px;
  position: absolute;
  bottom: -24px;
  left: -16px;
  background-color: transparent;
  color: ${(props) => props.theme.colors_ds.DANGER_GRAPH_DEFICIT};
`;

const CommonInputField = ({
  label,
  type,
  unit,
  handleChange,
  value,
  error,
  setFocusedIndex = () => {},
  name = "",
  inputWidth,
}: {
  label: string;
  type: "numeric" | "text" | "double-numeric"; // Added stricter typing for `type`
  unit?: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
  error?: string;
  setFocusedIndex?: (index: string | null) => void; // Optional prop for setting focused index
  name?: string;
  inputWidth?: number | string; // Optional override for the inner input width (default 85px)
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box component="form" noValidate autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <FormControl
        variant="outlined"
        fullWidth
        sx={{
          position: "relative",
          height: "40px",
        }}
      >
        <StyledInputLabel
          htmlFor={label}
          theme={theme}
          style={{
            transform: "translate(0, 0px) scale(1)",
          }}
        >
          {label}
        </StyledInputLabel>

        <StyledOutlinedInput
          id={label}
          value={value}
          inputProps={
            inputWidth != null
              ? {
                  style: {
                    width:
                      typeof inputWidth === "number"
                        ? `${inputWidth}px`
                        : inputWidth,
                  },
                }
              : undefined
          }
          type={type === "numeric" ? "number" : "text"} // Handle type mapping
          onFocus={() => {
            setFocused(true);
            setFocusedIndex(name);
          }}
          onBlur={() => {
            setFocused(false);
            setFocusedIndex(null);
          }}
          onChange={handleChange}
          inputRef={inputRef}
          theme={theme}
          has_error={error ? "true" : undefined}
          endAdornment={
            unit ? (
              <InputAdornment position="start" sx={{ margin: "0" }}>
                <span
                  style={{
                    fontFamily: "var(--font-Medium)",
                    fontSize: "12px",
                    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                    paddingTop: "19px",
                  }}
                >
                  {unit}
                </span>
              </InputAdornment>
            ) : null
          } // Added unit
        />

        {error && (
          <StyledFormHelperText theme={theme}>{error}</StyledFormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default CommonInputField;
